import Enumerable from "linq";
import { AbstractObjectEditor } from "../../../base/domain/system/document";
import { IPropertyPresentable, IPropertyEditable } from "../../../base/domain/system/edit";
import { IHistoryRecord, IBookmark, IObjectNameIdentifier, ILinkedObjectSpecifiable } from "../../../documaker/domain/format/objects";
import { IFormatSpecification } from "./IFormatSpecification";
import {
  IEditableFormatPageStructure,
  IEditableFormatObject,
} from "./IEditableFormatStructure";
import { IFormatObjectEditable } from "./IFormatObjectEditable";
import { ElementTypes } from "./objects";
import { HisInformationSetting } from "../../../base/domain/his";

export abstract class AbstractFormatObjectEditor<TObject extends IEditableFormatObject> extends AbstractObjectEditor<IEditableFormatPageStructure, IEditableFormatObject, TObject> implements IFormatObjectEditable {
  constructor(source: TObject | AbstractFormatObjectEditor<TObject>, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, isNew);
    this._specification = (source instanceof AbstractFormatObjectEditor)
      ? source._specification
      : specification;
    this._objectTypeName = this.createPropertyPresenter(s => s.elementName);
    this._id = this.createIdPropertyEditor(s => false);                                    // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._name = this.createNamePropertyEditor(s => false);                                // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._nameHistories = this.createNameHistoriesPropertyEditor(s => false);              // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._hisInformationKey = this.createInformationKeyPropertyEditor(s => false);         // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._bookmark = this.createBookmarkPropertyEditor(s => false);                        // 既定は編集不可に.必要な派生クラスでoverrideする想定.
  }

  protected _specification: IFormatSpecification;
  protected _objectTypeName: IPropertyPresentable<ElementTypes>;
  protected _id: IPropertyEditable<number>;
  protected _name: IPropertyEditable<string>;
  protected _nameHistories: IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]>;
  protected _bookmark: IPropertyEditable<IBookmark>;
  protected _hisInformationKey: IPropertyEditable<string>;

  protected createIdPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<number> {
    return this.createPropertyEditor(
      s => s.id,
      (s, v) => s.id = v,
      isWritable ? isWritable : s => true);
  }

  protected createNamePropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<string> {
    return this.createPropertyEditor(
      s => s.name,
      (s, v) => s.name = v,
      isWritable ? isWritable : s => true);
  }
  
  protected createInformationKeyPropertyEditor(isWritable?: (s: TObject) => boolean, onUpdated?: (s: TObject, v: string) => void): IPropertyEditable<string> {
    return this.createPropertyEditor(
      s => s.informationKey,
      (s, v) => {
        s.informationKey = v;
        if (onUpdated) {
          onUpdated(s, v);
        }
      },
      isWritable ? isWritable : s => true);
  }

  protected createNameHistoriesPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]> {
    return this.createPropertyEditor(
      s => s.nameHistories,
      (s, v) => s.nameHistories = v,
      isWritable ? isWritable : s => true);
  }

  protected createBookmarkPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IBookmark> {
    return this.createPropertyEditor(
      s => s.bookmark,
      (s, v) => s.bookmark = v,
      isWritable ? isWritable : s => true);
  }

  protected getSpecification(): IFormatSpecification {
    return this._specification;
  }

  getSelectableHisInformations(): HisInformationSetting[] {
    return [];  // 必要な派生クラスでオーバーライドする想定.
  }

  getSelectedHisInformation(): HisInformationSetting {
    return this.findSelectableHisInformation(this.hisInformationKey().getValue());
  }

  findSelectableHisInformation(key: string): HisInformationSetting {
    return Enumerable.from(this.getSelectableHisInformations())
      .firstOrDefault(i => i.key === key);
  }

  isHisInformationMixable(): boolean {
    return false; // 必要な派生クラスでオーバーライドする想定.
  }

  protected getHisInformationItemFromKey(key: string): HisInformationSetting {
    return Enumerable.from(this.getSelectableHisInformations()).firstOrDefault(s => s.key === key);
  }

  get isHisInformationItem(): boolean {
    return Enumerable.from(this.getSelectableHisInformations()).any(s => s.key === this._hisInformationKey.getValue());
  }

  objectTypeName(): IPropertyPresentable<ElementTypes> {
    return this._objectTypeName;
  }

  id(): IPropertyEditable<number> {
    return this._id;
  }

  name(): IPropertyEditable<string> {
    return this._name;
  }

  nameHistories(): IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]> {
    return this._nameHistories;
  }

  hisInformationKey(): IPropertyEditable<string> {
    return this._hisInformationKey;
  }

  getHisInformationName(): string {
    const hisInformationKey = this.hisInformationKey().getValue();
    return !this.isHisInformationItem
      ? hisInformationKey
      : this.getHisInformationItemFromKey(hisInformationKey).name;
  }

  bookmark(): IPropertyEditable<IBookmark> {
    return this._bookmark;
  }

  getWarningName(): string {
    // 派生クラスでオーバーライドする想定.
    return "";
  }

  getWarningSettingName(): string {
    // 派生クラスでオーバーライドする想定.
    return "";
  }

  isSettableWarningName(): boolean {
    // 派生クラスでオーバーライドする想定.
    return false;
  }

  getOverview(): string {
    // 派生クラスでオーバーライドする想定.(必要なら.)
    return this.getIdentifierOverview();
  }

  getTypeName(): string {
    // 派生クラスでオーバーライドする想定.
    return this.objectTypeName().getValue();
  }

  getIdentifiableName(): string {
    // 派生クラスでオーバーライドする想定.
    return this.name().getValue();
  }

  toLinkedObjectIdentifier(): ILinkedObjectSpecifiable {
    // 派生クラスでオーバーライドする想定.
    return {
      type: this.objectTypeName().getValue(),
      id: this.id().getValue(),
      key: this.name().getValue(),
    };
  }

  isAlreadyLayouted(): boolean {
    return !this.isNew();
  }

  protected getIdentifiableHisName(): string {
    const key = this.hisInformationKey().getValue();
    const setting = this.getHisInformationItemFromKey(key);
    return !!setting
      ? setting.name
      : key;
  }

  protected getIdentifierOverview(): string {
    const name = this.getIdentifiableName();
    return this.getTypeName()
        + this.get(s => s.id ? (" ID:" + s.id.toString()) : "", "")
        + (name ? (" 名称:" + name) : "");
  }

  protected toEntities(source: IEditableFormatPageStructure): IEditableFormatObject[] {
    return source.objects;
  }

  protected toEntity(source: IEditableFormatPageStructure, entity: TObject): TObject {
    // ソースのインスタンスは置き換えない前提.インスタンス一致で判定.置き換えるような設計変更があるならユニークなキーを用意する事.
    return Enumerable.from(source.objects).firstOrDefault(p => p === entity) as TObject;
  }

  abstract clone(): IFormatObjectEditable;
}
